import React, { useEffect, useMemo, useRef, useState } from 'react';
import { authHeaders, clearAuth } from '../../services/auth';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ScrollText, AlertCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GrievanceBlock = ({ contact }) => {
  if (!contact) return null;
  return (
    <div
      className="my-3 p-4 rounded-lg border border-slate-200 bg-slate-50"
      data-testid="grievance-contact-block"
    >
      <div className="space-y-1 text-sm">
        <div><span className="font-semibold text-slate-700">Name:</span> <span className="text-slate-900">{contact.name}</span></div>
        <div><span className="font-semibold text-slate-700">Email:</span> <a href={`mailto:${contact.email}`} className="text-blue-700 hover:underline">{contact.email}</a></div>
        <div><span className="font-semibold text-slate-700">Phone:</span> <a href={`tel:${contact.phone}`} className="text-blue-700 hover:underline">{contact.phone}</a></div>
        {contact.source === 'global' && (
          <div className="text-xs text-amber-700 mt-1">
            (Community-specific contact not yet configured — showing platform default.)
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ section, contact }) => (
  <section
    id={`tc-${section.id}`}
    data-testid={`tc-section-${section.id}`}
    className={`mb-5 ${section.highlight ? 'p-4 rounded-lg border border-amber-300 bg-amber-50' : ''}`}
  >
    <h3 className="text-base font-semibold text-slate-900 mb-2">{section.title}</h3>
    {(section.body || []).map((p, i) =>
      p === '__GRIEVANCE_CONTACT_BLOCK__' ? (
        <GrievanceBlock key={i} contact={contact} />
      ) : (
        <p
          key={i}
          className={`text-sm leading-relaxed mb-2 ${section.highlight ? 'text-slate-800 font-medium' : 'text-slate-700'}`}
        >
          {p}
        </p>
      )
    )}
  </section>
);

export const TCAcceptanceModal = ({ user, onAccepted }) => {
  const [doc, setDoc] = useState(null); // {version, effective_date, sections}
  const [contact, setContact] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [accepted, setAccepted] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    Promise.all([
      fetch(`${API}/api/tc/document`).then((r) => r.ok ? r.json() : Promise.reject(new Error(`document fetch failed: ${r.status}`))),
      fetch(`${API}/api/tc/current`, { headers: authHeaders(), credentials: 'include' }).then((r) => r.ok ? r.json() : null),
    ]).then(([docRes, currentRes]) => {
      if (cancelled) return;
      if (docRes && docRes.ok && Array.isArray(docRes.sections) && docRes.sections.length > 0) {
        setDoc(docRes);
      } else {
        setLoadError(true);
      }
      if (currentRes && currentRes.ok) setContact(currentRes.grievance_contact);
    }).catch((e) => {
      if (!cancelled) {
        if (process.env.NODE_ENV !== 'production') console.error('TC load failed:', e);
        setLoadError(true);
      }
    });
    return () => { cancelled = true; };
  }, [loadAttempt]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setScrolledToBottom(true);
    }
  };

  const canSubmit = useMemo(
    () => accepted && scrolledToBottom && !submitting && !!doc,
    [accepted, scrolledToBottom, submitting, doc]
  );

  const handleAccept = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/tc/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tc_version: doc.version,
          consents: { general: true },
          villa_number: user?.villa_number || null,
        }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.ok) {
        setError(j.detail || 'Failed to record acceptance. Please try again.');
        setSubmitting(false);
        return;
      }
      onAccepted();
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  const handleDecline = () => {
    if (!window.confirm(
      'You must accept the Terms & Conditions to use GateKeeper Pro. Declining will sign you out. Continue?'
    )) return;
    clearAuth();
    window.location.href = '/login';
  };

  return (
    <div
      className="fixed inset-0 z-[10000] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      data-testid="tc-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tc-modal-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
        data-testid="tc-modal"
      >
        <div className="px-6 pt-5 pb-3 border-b border-slate-200 flex items-start gap-3 shrink-0">
          <div className="w-10 h-10 rounded-lg bg-gk-teal/10 flex items-center justify-center shrink-0">
            <ScrollText className="w-5 h-5 text-gk-teal" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="tc-modal-title" className="text-lg font-bold text-slate-900">
              Terms & Conditions
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {doc ? `Version ${doc.version} · Effective ${doc.effective_date}` : 'Loading…'}
            </p>
            <p className="text-xs text-slate-600 mt-1">
              Please read and accept these Terms before using GateKeeper Pro.
            </p>
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex-1 overflow-y-auto px-6 py-4"
          data-testid="tc-scroll-area"
        >
          {!doc ? (
            loadError ? (
              <div className="py-10 text-center" data-testid="tc-load-error">
                <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-3" />
                <p className="text-sm font-semibold text-slate-900 mb-1">
                  Could not load Terms & Conditions
                </p>
                <p className="text-xs text-slate-500 mb-4 max-w-md mx-auto">
                  Please check your internet connection and try again. If the problem persists, contact your community management.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setLoadAttempt((n) => n + 1)}
                  data-testid="tc-load-retry"
                >
                  Retry
                </Button>
              </div>
            ) : (
              <div className="text-sm text-slate-500 py-8 text-center">Loading document…</div>
            )
          ) : (
            <>
              {(doc.sections || []).map((s) => (
                <Section key={s.id} section={s} contact={contact} />
              ))}
              <div className="text-center text-xs text-slate-400 pt-2 pb-1">— End of document —</div>
            </>
          )}
        </div>

        <div className="border-t border-slate-200 bg-slate-50 px-6 py-4 space-y-3 shrink-0">
          {!scrolledToBottom && doc && (
            <div className="text-xs text-amber-700 flex items-center gap-1.5" data-testid="tc-scroll-hint">
              <AlertCircle className="w-3.5 h-3.5" /> Scroll to the bottom of the document to enable the consent boxes.
            </div>
          )}
          <label className={`flex items-start gap-3 ${!scrolledToBottom ? 'opacity-50 pointer-events-none' : ''}`}>
            <Checkbox
              checked={accepted}
              onCheckedChange={(v) => setAccepted(!!v)}
              className="mt-0.5"
              data-testid="tc-consent-general"
            />
            <span className="text-sm text-slate-800">
              I have read and agree to the GateKeeper Pro{' '}
              <a
                href="/legal"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gk-teal hover:text-gk-teal-hover font-medium underline underline-offset-2"
                data-testid="tc-link-public"
                onClick={(e) => e.stopPropagation()}
              >
                Terms &amp; Conditions
              </a>
              .
            </span>
          </label>

          {error && (
            <div className="text-xs text-red-600 flex items-center gap-1.5" data-testid="tc-error">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
            <Button
              variant="ghost"
              onClick={handleDecline}
              data-testid="tc-decline-button"
              className="text-slate-600 hover:text-slate-900"
            >
              Decline & sign out
            </Button>
            <Button
              onClick={handleAccept}
              disabled={!canSubmit}
              data-testid="tc-accept-button"
              className="bg-gk-teal hover:bg-gk-teal-hover text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recording…</>
              ) : (
                'I Agree & Continue'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TCAcceptanceModal;

